'use client';

import { useEffect, useRef } from 'react';

import Image from 'next/image';

import { cn } from '@/lib/utils';

import { FEATURE_STORIES } from '../../constants/landing-constants';

const mediaFrameClassName =
	'shrink-0 overflow-hidden rounded-[min(1vw,12px)] outline-1 -outline-offset-1 outline-black/5 w-full md:w-[28rem]';

const FeatureImage = ({ src, label }: { src: string; label: string }) => {
	return (
		<div className={mediaFrameClassName}>
			<Image
				src={src}
				alt={label}
				width={1120}
				height={720}
				unoptimized
				sizes="(min-width: 768px) 448px, 90vw"
				className="h-auto w-full object-contain"
			/>
		</div>
	);
};

const FeatureVideo = ({ src, poster, label }: { src: string; poster: string; label: string }) => {
	const videoRef = useRef<HTMLVideoElement>(null);

	// Below the fold: loading eagerly competes with the hero for bandwidth and hurts LCP.
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) video.play().catch(() => {});
				else video.pause();
			},
			{ rootMargin: '200px 0px' }
		);

		observer.observe(video);
		return () => observer.disconnect();
	}, []);

	return (
		<div className={mediaFrameClassName}>
			<video
				ref={videoRef}
				src={src}
				poster={poster}
				muted
				loop
				playsInline
				preload="none"
				aria-label={label}
				width={1120}
				height={720}
				className="aspect-[1120/720] h-auto w-full object-contain"
			/>
		</div>
	);
};

type FeaturesSectionProps = {
	/** Story ids to leave out, e.g. one already shown elsewhere on the page. */
	excludeIds?: string[];
	/** Heading size override, so SEO pages can match their own section headings. */
	headingClassName?: string;
};

export const FeaturesSection = ({ excludeIds, headingClassName }: FeaturesSectionProps = {}) => {
	const stories = excludeIds?.length
		? FEATURE_STORIES.filter((story) => !excludeIds.includes(story.id))
		: FEATURE_STORIES;

	return (
		<section className="landing-width mb-24 w-full pt-12 md:mb-32 md:pt-20">
			<h2
				className={cn(
					'landing-heading mb-16 text-balance text-center !text-[32px] tracking-tight md:mb-24 md:!text-[46px]',
					headingClassName
				)}
			>
				Everything you need to run your hiring
			</h2>
			<ul className="mx-auto flex w-full max-w-3xl flex-col gap-10 md:max-w-4xl md:gap-16">
				{stories.map((feature) => (
					<li
						key={feature.heading}
						className="flex w-full flex-col items-start gap-4 md:flex-row md:gap-8"
					>
						<div className="flex min-w-0 flex-1 flex-col items-start gap-1 md:gap-5 md:self-center">
							<h3 className="text-lg font-medium tracking-tight text-primary md:text-[2rem]">
								{feature.heading}
							</h3>
							<p className="text-base leading-relaxed text-secondary md:text-xl">
								{feature.description}
							</p>
						</div>
						{feature.media.type === 'video' ? (
							<FeatureVideo
								src={feature.media.src}
								poster={feature.media.poster}
								label={feature.heading}
							/>
						) : (
							<FeatureImage
								src={feature.media.src}
								label={feature.heading}
							/>
						)}
					</li>
				))}
			</ul>
		</section>
	);
};
